<template>
  <div
    class="relative select-none"
    :style="{ width: `${size}px`, height: `${dialHeight}px` }"
    role="img"
    :aria-label="ariaLabel"
  >
    <svg :viewBox="`0 0 200 ${VH}`" class="block w-full h-auto">
      <!-- Segmentos do arco semicircular (-50 à esquerda, +50 à direita) -->
      <path
        v-for="s in segments"
        :key="s.i"
        :d="s.d"
        fill="none"
        :stroke="s.color"
        :stroke-width="SEG_W"
        stroke-linecap="butt"
        :opacity="segOpacity"
        class="transition-opacity duration-150"
      />
      <!-- Agulha -->
      <line
        v-if="tunerState.note"
        :x1="needle.x1"
        :y1="needle.y1"
        :x2="needle.x2"
        :y2="needle.y2"
        :stroke="needleColor"
        :stroke-width="compact ? 3 : 4"
        stroke-linecap="round"
        :style="inTune ? 'filter: drop-shadow(0 0 5px rgba(34,197,94,.8))' : ''"
      />
      <!-- Rótulos das extremidades -->
      <text
        v-if="!compact"
        x="14"
        y="136"
        text-anchor="middle"
        font-size="10"
        font-weight="700"
        fill="#9ca3af"
      >-50</text>
      <text
        v-if="!compact"
        x="100"
        y="136"
        text-anchor="middle"
        font-size="10"
        font-weight="700"
        fill="#9ca3af"
      >0</text>
      <text
        v-if="!compact"
        x="186"
        y="136"
        text-anchor="middle"
        font-size="10"
        font-weight="700"
        fill="#9ca3af"
      >+50</text>
    </svg>

    <!-- Centro: nota + frequência + status -->
    <div
      class="absolute inset-x-0 text-center pointer-events-none"
      :style="{ top: compact ? '38%' : '42%' }"
    >
      <div
        v-if="tunerState.note"
        class="font-black tracking-tight leading-none"
        :class="[compact ? 'text-lg' : 'text-5xl', dark ? 'text-white' : 'text-gray-900']"
      >
        {{ tunerState.note.name }}<sub :class="compact ? 'text-[10px]' : 'text-xl'">{{ tunerState.note.octave }}</sub>
      </div>
      <div
        v-else
        class="font-medium italic"
        :class="[compact ? 'text-[10px]' : 'text-sm', dark ? 'text-slate-400' : 'text-gray-400']"
      >
        {{ tunerState.isListening ? '...' : '--' }}
      </div>
      <template v-if="!compact && tunerState.note">
        <div
          class="font-semibold mt-1"
          :class="['text-sm', dark ? 'text-slate-300' : 'text-gray-700']"
        >
          {{ hzLabel }} Hz
        </div>
        <div
          class="text-sm font-bold"
          :style="{ color: statusColor }"
        >
          {{ statusLabel }}
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps } from 'vue';
import { useI18n } from 'vue-i18n';
import { tunerService } from '@/service/audio/tuner.service';

const props = defineProps({
  size: { type: Number, default: 260 },
  compact: { type: Boolean, default: false },
  dark: { type: Boolean, default: false },
});

const tunerState = tunerService.state;
const { t } = useI18n();

// Geometria: semicírculo de -90° (esquerda) a +90° (direita), 0° = topo.
const VH = 142;
const CX = 100;
const CY = 112;
const R = 78;
const SEG_W = 17;
const N_SEGS = 15;
const SLOT = 180 / N_SEGS;
const GAP = 2.5;

const clampedCents = computed(() => Math.max(-50, Math.min(50, tunerState.smoothedCents || 0)));
const needleAngle = computed(() => (clampedCents.value / 50) * 90);
const inTune = computed(() => tunerState.inTune);

const dialHeight = computed(() => Math.round((props.size * VH) / 200));

function polar(r, angleDeg) {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: CX + r * Math.sin(rad), y: CY - r * Math.cos(rad) };
}

function arcPath(r, fromDeg, toDeg) {
  const a = polar(r, fromDeg);
  const b = polar(r, toDeg);
  const large = Math.abs(toDeg - fromDeg) > 180 ? 1 : 0;
  const sweep = toDeg >= fromDeg ? 1 : 0;
  return `M ${a.x.toFixed(2)} ${a.y.toFixed(2)} A ${r} ${r} 0 ${large} ${sweep} ${b.x.toFixed(2)} ${b.y.toFixed(2)}`;
}

function segColor(dist) {
  if (dist <= 1) return '#22c55e';
  if (dist === 2) return '#84cc16';
  if (dist === 3) return '#eab308';
  if (dist === 4) return '#f59e0b';
  if (dist === 5) return '#f97316';
  return '#ef4444';
}

const segments = computed(() => {
  const list = [];
  for (let i = 0; i < N_SEGS; i++) {
    const from = -90 + i * SLOT + GAP / 2;
    const to = -90 + (i + 1) * SLOT - GAP / 2;
    list.push({ i, d: arcPath(R, from, to), color: segColor(Math.abs(i - (N_SEGS - 1) / 2)) });
  }
  return list;
});

const segOpacity = computed(() => (tunerState.note ? 1 : 0.4));

const needle = computed(() => {
  const p1 = polar(R - (props.compact ? 26 : 32), needleAngle.value);
  const p2 = polar(R + 5, needleAngle.value);
  return { x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y };
});

const needleColor = computed(() => (inTune.value ? '#16a34a' : '#374151'));

const hzLabel = computed(() => {
  const f = tunerState.liveFrequency || 0;
  return f > 0 ? f.toFixed(0) : '--';
});

const statusColor = computed(() => {
  if (inTune.value) return '#16a34a';
  const abs = Math.abs(clampedCents.value);
  if (abs <= 15) return '#d97706';
  return '#dc2626';
});

const statusLabel = computed(() => {
  if (inTune.value) return `${t('tuner.inTune')} ✓`;
  const c = tunerState.smoothedCents || 0;
  return `${c > 0 ? '+' : ''}${c} ${t('tuner.cents')}`;
});

const ariaLabel = computed(() => {
  if (!tunerState.note) return t('tuner.ariaNoSignal');
  return t('tuner.ariaLabel', { note: `${tunerState.note.name}${tunerState.note.octave}`, status: statusLabel.value });
});
</script>
